import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import type { useZeroXWithdrawal } from '../../../hooks/payment/useZeroXWithdrawal';
import useLocalization from '../../../hooks/useLocalization';
import { zeroXCashierStyles as styles } from '../../../styles/components/zeroXCashier';
import { zeroXCashierFieldStyles } from '../../../styles/components/zeroXCashier';
import ZeroXActionButton from './ZeroXActionButton';
import { mergeSx } from '../../../utils/styles';
import FormNumberInput from '../../molecules/form-elements/FormNumberInput';

type WithdrawalEditingProps = {
  controller: ReturnType<typeof useZeroXWithdrawal>;
};

const ZeroXWithdrawalEditing = ({ controller }: WithdrawalEditingProps) => {
  const { t } = useLocalization();
  const { text: textFieldSx, select: selectFieldSx, formInset } = useSkinStyles(zeroXCashierFieldStyles);
  const {
    phase,
    message,
    form,
    currencies,
    requiresTag,
    selectedCurrency,
    accountCurrency,
    isAmountValid,
    changeField,
    requestPreview,
  } = controller;

  const options = currencies.data ?? [];
  const unavailable = currencies.isError || (!currencies.isLoading && options.length === 0);
  const busy = phase.name === 'previewing';

  const minimumHelp =
    selectedCurrency &&
    t('zeroXMinimumAssetAmount', {
      minimum: selectedCurrency.minimumWithdrawalAmount,
      currency: selectedCurrency.label,
    });

  return (
    <Stack component='form' onSubmit={requestPreview} sx={mergeSx(styles.root, formInset)} aria-busy={busy}>
      <FormNumberInput
        control={form.control}
        name='amount'
        onChange={(value) => changeField('amount', value)}
        label={t('zeroXAccountAmountCurrency', { currency: accountCurrency })}
        sx={textFieldSx}
        disabled={busy}
      />

      <Controller
        control={form.control}
        name='currency'
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            onChange={(event) => changeField('currency', event.target.value)}
            select
            label={t('zeroXCurrency')}
            sx={selectFieldSx}
            aria-busy={currencies.isLoading}
            disabled={busy || currencies.isLoading || unavailable}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? minimumHelp}
          >
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        control={form.control}
        name='address'
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            onChange={(event) => changeField('address', event.target.value)}
            label={t('walletAddress')}
            sx={textFieldSx}
            disabled={busy}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
          />
        )}
      />

      {requiresTag && (
        <Controller
          control={form.control}
          name='tag'
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              onChange={(event) => changeField('tag', event.target.value)}
              required
              label={t('zeroXDestinationTag')}
              sx={textFieldSx}
              disabled={busy}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      )}

      {unavailable && (
        <Alert
          severity='info'
          action={
            <Button variant='text' onClick={() => void currencies.refetch()}>
              {t('retry')}
            </Button>
          }
        >
          {t('zeroXWithdrawalUnavailable')}
        </Alert>
      )}

      {message && <Alert severity={message.severity}>{t(message.key, message.values)}</Alert>}

      <Stack sx={styles.actions}>
        <ZeroXActionButton
          type='submit'
          loading={busy}
          disabled={currencies.isLoading || unavailable || !isAmountValid}
          label={t(busy ? 'zeroXPreparingQuote' : 'zeroXReviewWithdrawal')}
        />
      </Stack>
    </Stack>
  );
};

export default ZeroXWithdrawalEditing;
